import type { ApiError } from './problem-details'

/**
 * Friendly text for the API's stable dotted error codes.
 *
 * The server's wording is written for an operator reading a log; this table is written for a
 * teller with a customer waiting at the counter, so each entry says what happened *and* what to
 * do next. A code missing from the table is not an error — {@link resolveErrorMessage} falls back
 * to the problem's own detail, then its title, then a generic line — which is what keeps this
 * file from having to stay exhaustive as the backend grows.
 */
const MESSAGES: Record<string, string> = {
  // Authentication and session
  'auth.invalid_credentials': 'That username and password do not match. Check both and try again.',
  'auth.account_locked':
    'This account is locked after repeated failed sign-ins. An administrator must unlock it.',
  'auth.account_disabled': 'This account has been disabled. Contact your administrator.',
  'auth.mfa_required': 'Enter the six-digit code from your authenticator app.',
  'auth.mfa_invalid_code': 'That code was not accepted. Codes expire after 30 seconds — try the next one.',
  'auth.mfa_not_enrolled':
    'Your role requires an authenticator app. Sign in on the portal and it will walk you through setting one up.',
  'auth.password_change_required': 'You must set a new password before continuing.',
  'auth.password_rejected':
    'That password is too common or has appeared in a breach. Choose a different one.',
  'auth.password_reused': 'You cannot reuse a recent password.',
  'auth.session_expired': 'Your session has ended. Sign in again to continue.',
  'auth.refresh_reuse_detected':
    'This session was signed out for your protection. Sign in again, and tell your administrator if this repeats.',

  // Access control and dual authorisation
  'access.permission_denied': 'Your role does not allow this action.',
  'access.branch_not_permitted': 'You are not authorised to operate on this branch.',
  'access.maker_is_checker': 'You cannot approve a request you submitted yourself.',
  'access.approval_expired': 'This approval request has expired. The maker must submit it again.',
  'access.approval_payload_changed':
    'The request changed after it was submitted, so the approval no longer applies.',
  'access.role_conflict':
    'These roles cannot be held by the same user — they breach a segregation-of-duties rule.',

  // Ledger and period control
  'ledger.period_closed': 'The accounting period is closed. Nothing can be posted into it.',
  'ledger.business_day_closed':
    'The branch business day is closed. Reopen it, or post into the current day with a value date.',
  'ledger.unbalanced_journal': 'The journal does not balance — debits and credits must be equal.',
  'ledger.account_not_postable': 'That account is a heading, not a postable account.',
  'ledger.day_close_blocked':
    'The day cannot close yet: a till is unbalanced or transactions are still awaiting approval.',

  // Loans
  'loans.not_active': 'That loan is not active, so no repayment can be posted against it.',
  'loans.security_deposit_insufficient':
    'The security deposit is below the amount this product requires. Take a top-up first.',
  'loans.group_health_below_threshold':
    'The group health score is below the qualifying threshold for a new round.',
  'loans.principal_growth_exceeded':
    'The requested principal exceeds the growth cap for the next round on this account.',
  'loans.prerequisite_missing':
    'This loan account is missing a prerequisite: an active savings account, a security deposit account, or a passbook.',
  'loans.already_disbursed': 'This loan cycle has already been disbursed.',
  'loans.overpayment': 'The amount exceeds what is outstanding. The excess must be routed deliberately.',
  'loans.schedule_not_generated': 'The repayment schedule has not been generated for this cycle yet.',

  // Savings
  'savings.insufficient_available_balance':
    'The available balance does not cover this withdrawal. Holds and security deposits are excluded from it.',
  'savings.account_frozen': 'This account is frozen. A supervisor must release it.',
  'savings.withdrawal_not_permitted':
    'A security-deposit account cannot be withdrawn from. It is released by an offset or at closure.',
  'savings.account_dormant': 'This account is dormant and must be reactivated before it can transact.',

  // Tellering
  'teller.session_not_open': 'Open your till before posting cash transactions.',
  'teller.till_unbalanced': 'The declared cash does not match the expected balance.',
  'teller.limit_exceeded': 'This amount is above your limit. It needs a supervisor override.',

  // Customers, KYC and groups
  'customers.ghana_card_duplicate':
    'A customer with this Ghana Card number already exists — the same person cannot be onboarded twice.',
  'customers.kyc_incomplete': 'KYC is incomplete. All mandatory fields and documents are required first.',
  'customers.kyc_review_overdue':
    'This customer’s KYC review is overdue, which blocks new disbursements until it is refreshed.',
  'customers.screening_match_unresolved':
    'A screening match is unresolved. Compliance must dispose of it before this account can be approved.',

  // Groups. Admission passes the Customers module's own refusals through unchanged — a blacklisted
  // customer is `customers.blacklisted` wherever it is raised — so only the group-specific codes
  // are worded here.
  'groups.not_found': 'That group could not be found, or it belongs to a branch you cannot see.',
  'groups.member_kyc_not_approved':
    'Every member is KYC’d individually, and this customer’s file is not approved. Approve the file first, then add them.',
  'groups.member_already_active': 'This customer is already an active member of this group.',
  'groups.member_in_another_group':
    'This customer is already an active member of another group. One active group per customer.',
  'groups.member_not_found': 'That customer is not a member of this group.',
  'groups.leader_not_a_member': 'The leader has to be an active member of the group.',
  'groups.leader_already': 'That member already leads this group.',
  'groups.leader_must_be_replaced':
    'Appoint another leader first. A leader cannot be recorded as leaving while the group would be left with no signatory.',
  'groups.leader_required': 'A group cannot become active without a leader.',
  'groups.officer_not_a_loan_officer':
    'That member of staff is not flagged as a loan officer. An administrator sets that flag on the user record.',
  'groups.officer_wrong_branch': 'That officer cannot act for this group’s branch.',
  'groups.dissolved': 'This group is dissolved and cannot be changed.',
  'groups.formation_date_in_future': 'A group cannot have been formed on a date that has not happened.',
  'groups.meeting_location_required':
    'Set the group’s meeting location before scheduling meetings — a meeting with no place is not one anyone can be sent to.',
  'groups.meeting_not_open_for_attendance':
    'This meeting is already completed or was cancelled, so its register is closed. A register is recorded once.',
  'groups.attendance_member_not_in_group':
    'Someone on the register was not a member of the group on the meeting date.',
  'groups.attendance_duplicate_member': 'The same member appears twice on the register.',
  'groups.geofence_violation':
    'The check-in position is outside the meeting location’s radius, so the register cannot be unlocked there.',
  'groups.geofence_radius_out_of_range': 'A geofence radius must be between 20 and 2000 metres.',
  'groups.coordinates_invalid': 'Those coordinates are not a point on the earth. Check the pin.',
  'groups.schedule_day_of_month_out_of_range':
    'A monthly meeting day must be between 1 and 28 — a group that meets on the 31st does not meet in February.',
  'groups.health.insufficient_history':
    'There is too little history in the window to score this group. That is not a low score, it is no score.',
  'groups.health.credit_data_unavailable':
    'The loan book cannot answer for this window, so the group cannot be scored yet.',

  // Field and paper collection
  'collections.sheet_not_open': 'That collection sheet is not open for capture.',
  'collections.duplicate_capture': 'This line has already been captured. Check the batch before re-entering it.',

  // Cross-cutting
  'common.validation_failed': 'Some entries need correcting before this can be submitted.',
  'common.concurrency_conflict':
    'Someone else changed this record while you were editing. Reload to see the current values.',
  'common.idempotency_replay': 'This operation was already applied — it has not been posted twice.',
  'common.rate_limited': 'Too many attempts. Wait a moment before trying again.',
  'network.unreachable': 'No connection to the server. Check the branch link and try again.',
}

const STATUS_FALLBACK: Record<number, string> = {
  401: 'Your session has ended. Sign in again to continue.',
  403: 'Your role does not allow this action.',
  404: 'That record could not be found, or it belongs to a branch you cannot see.',
  409: 'Someone else changed this record first. Reload and try again.',
  429: 'Too many requests. Wait a moment before trying again.',
  500: 'The server could not complete the request. The technical team has the details.',
  502: 'The server is unreachable.',
  503: 'The service is temporarily unavailable. Try again shortly.',
  504: 'The server took too long to respond.',
}

/** True when the code is one the application has friendly wording for. */
export const hasFriendlyMessage = (code: string) => code in MESSAGES

/**
 * The message shown to the user. The fallback chain deliberately ends in something actionable
 * rather than in a raw status code — an unmapped failure still has to be explainable at a
 * counter.
 */
export function resolveErrorMessage(error: ApiError): string {
  return (
    MESSAGES[error.code] ??
    error.detail ??
    STATUS_FALLBACK[error.status] ??
    error.title ??
    'Something went wrong. Try again, and report it if it persists.'
  )
}
